import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const gear1Ref = useRef<HTMLDivElement>(null);
  const gear2Ref = useRef<HTMLDivElement>(null);
  const gear3Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let angle1 = 0;
    let angle2 = 0;
    let angle3 = 0;
    let raf: number;

    const animate = () => {
      angle1 += 0.15;
      angle2 -= 0.1;
      angle3 += 0.08;
      if (gear1Ref.current) gear1Ref.current.style.transform = `rotate(${angle1}deg)`;
      if (gear2Ref.current) gear2Ref.current.style.transform = `rotate(${angle2}deg)`;
      if (gear3Ref.current) gear3Ref.current.style.transform = `rotate(${angle3}deg)`;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleScroll = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #111 100%)' }}
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20industrial%20engineering%20workshop%20background%20with%20metal%20textures%20gears%20bolts%20screws%20mechanical%20parts%20abstract%20dark%20atmosphere%20professional%20technology%20aesthetic%20moody%20lighting%20deep%20shadows%20steel%20surfaces%20minimalist%20industrial%20design&width=1920&height=1080&seq=hero-bg-001&orientation=landscape"
          alt="hero background"
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
      </div>

      {/* Grid pattern */}
      <div
        className="absolute inset-0 z-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      ></div>

      {/* Yellow accent line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-yellow-400 z-10"></div>

      {/* Floating gears */}
      <div className="absolute right-8 top-24 z-10 opacity-30 pointer-events-none select-none">
        <div ref={gear1Ref} className="w-48 h-48 flex items-center justify-center">
          <i className="ri-settings-3-line text-yellow-400" style={{ fontSize: '180px' }}></i>
        </div>
      </div>
      <div className="absolute right-48 top-48 z-10 opacity-20 pointer-events-none select-none">
        <div ref={gear2Ref} className="w-24 h-24 flex items-center justify-center">
          <i className="ri-settings-2-line text-yellow-300" style={{ fontSize: '90px' }}></i>
        </div>
      </div>
      <div className="absolute right-16 bottom-32 z-10 opacity-15 pointer-events-none select-none">
        <div ref={gear3Ref} className="w-32 h-32 flex items-center justify-center">
          <i className="ri-settings-line text-white" style={{ fontSize: '120px' }}></i>
        </div>
      </div>

      {/* Screw decorations */}
      {[
        { top: '20%', left: '5%', size: 'text-2xl', opacity: 'opacity-20' },
        { top: '70%', left: '8%', size: 'text-xl', opacity: 'opacity-15' },
        { top: '40%', right: '5%', size: 'text-3xl', opacity: 'opacity-10' },
        { top: '85%', right: '20%', size: 'text-2xl', opacity: 'opacity-20' },
      ].map((pos, i) => (
        <div
          key={i}
          className={`absolute z-10 pointer-events-none select-none ${pos.opacity}`}
          style={{ top: pos.top, left: pos.left, right: (pos as { right?: string }).right }}
        >
          <i className={`ri-tools-line text-yellow-300 ${pos.size}`}></i>
        </div>
      ))}

      {/* Main content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 md:px-8 w-full pt-24 pb-16">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/40 rounded-full px-4 py-1.5 mb-8">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-settings-3-line text-yellow-400 text-sm animate-spin" style={{ animationDuration: '3s' }}></i>
            </div>
            <span className="text-yellow-400 text-xs font-semibold tracking-widest uppercase">IT Solutions from Osaka</span>
          </div>

          {/* Main heading */}
          <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Montserrat', 'Noto Sans JP', sans-serif" }}>
            技術で未来を<br />
            <span className="text-yellow-400">デザイン</span>する
          </h1>

          <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-10 max-w-xl">
            大阪発のITソリューション企業として、<br className="hidden md:block" />
            Webサイト制作からシステム開発まで、<br className="hidden md:block" />
            お客様のビジネス成長を全力でサポートします。
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => handleScroll('services')}
              className="inline-flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold px-8 py-4 rounded-full hover:bg-yellow-300 transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-settings-3-line animate-spin" style={{ animationDuration: '3s' }}></i>
              </div>
              サービスを見る
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </button>
            <button
              onClick={() => handleScroll('contact')}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-bold px-8 py-4 rounded-full hover:border-yellow-400 hover:text-yellow-400 transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
            >
              お問い合わせ
            </button>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-8 mt-16 pt-8 border-t border-white/10">
            {[
              { num: '2022', label: '設立年' },
              { num: '6+', label: 'サービス領域' },
              { num: '24/7', label: 'サポート体制' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl font-black text-yellow-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.num}</div>
                <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 opacity-60">
        <span className="text-white text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white to-transparent animate-pulse"></div>
      </div>
    </section>
  );
}
